import React, { useState, useEffect } from "react";
import {
  Card,
  Col,
  Table,
  Button,
  Form,
  Row,
  Breadcrumb,
  Spinner,
  Modal
} from "react-bootstrap";
import FalconCloseButton from "components/common/falconCloseButton";
import { useForm, FormProvider, useFieldArray } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import Background from "components/common/background";
import corner4 from "assets/img/illustrations/corner-4.png";
import Divider from "components/common/divider";
import { useTranslation } from "react-i18next";
import paths from "routes/paths";
import IconButton from "components/common/iconButton";
import { fetchRoleAndClaims, handleLoadingAndAccess, generateGUID } from "../../assets/js/common";

const EditTask = () => {
  const { taskId: taskIdParam } = useParams();
  const taskId = (taskIdParam || "").toUpperCase();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [taskData, setTaskData] = useState(null);
  const [selectedIndexes, setSelectedIndexes] = useState([]);
  const [deletedSubtaskIds, setDeletedSubtaskIds] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  const schema = yup
    .object({
      task: yup.string().required(t("taskNameRequired")),
      subTasks: yup.array()
        .of(
          yup.object().shape({
            value: yup.string().required(t("subTasksRequired")).trim()
          })
        )
        .min(1, t("subTasksRequired"))
        .test("at-least-one-filled", t("subTasksRequired"), (subTasks) =>
          Array.isArray(subTasks) && subTasks.some((s) => s.value.trim() !== "")
        )
    })
    .required();


  useEffect(() => {
    fetchRoleAndClaims({
      navigate,
      claimType: "workTasks",
      claimValue: "edit",
      setHasAccess,
      setIsLoading,
    });
  }, [navigate]);

  const methods = useForm({
    mode: "onChange",
    resolver: yupResolver(schema),
    defaultValues: {
      task: "",
      subTasks: [{ value: "" }],
    },
  });

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    control,
    watch,
    formState: { errors },
    trigger,
    clearErrors,
  } = methods;

  const { fields, append, remove } = useFieldArray({
    control,
    name: "subTasks",
  });

  const fetchTaskData = async (taskId) => {
    try {
      const taskRes = await axios.get(
        `${process.env.REACT_APP_API_URL}/taskRoute/getTaskById/${taskId}`,{
          headers: {
            Authorization: process.env.REACT_APP_BasicAuthSecret
          }
        }
      );
      setTaskData(taskRes.data);
      setValue("task", taskRes.data.TaskName);
    } catch (err) {
      console.error("Failed to load task", err);
      toast.error(t("failedToLoadTaskDetails"));
    }
  };

  const fetchSubtaskData = async (taskId) => {
    try {
      const subtaskRes = await axios.get(
        `${process.env.REACT_APP_API_URL}/subtaskRoute/subtasks/${taskId}`,{
          headers: {
            Authorization: process.env.REACT_APP_BasicAuthSecret
          }
        }
      );

      if (subtaskRes.data.length > 0) {
        setValue(
          "subTasks",
          subtaskRes.data.map((sub) => ({
            value: sub.Subtask,
            originalValue: sub.Subtask, // Needed for change detection
            SubtaskId: sub.SubtaskId,
          }))
        );
      } else {
        setValue("subTasks", [{ value: "" }]); // At least one row
      }
    } catch (err) {
      console.error("Failed to load subtasks", err);
      toast.error(t("failedToLoadSubtaskDetails"));
    }
  };

  useEffect(() => {
    fetchTaskData(taskId);
    fetchSubtaskData(taskId);
    setLoading(false);
  }, [taskId]);

  // Watch subtasks to clear errors on valid input
  const subTasks = watch("subTasks");

  useEffect(() => {
    const hasError =
      Array.isArray(errors.subTasks) &&
      errors.subTasks.some((item) => item && Object.keys(item).length > 0);

    const hasEmpty = subTasks?.some((subtask) => !subtask.value.trim());

    if (!hasEmpty && hasError) {
      clearErrors("subTasks");
    }
  }, [subTasks, errors.subTasks, clearErrors]);

  // Add new subtask only if last subtask row has value
  const handleAddSubtask = async () => {
    const current = watch("subTasks");
    const hasEmpty = current.some(subtask => !subtask.value?.trim());

    if (hasEmpty) {
      await trigger("subTasks");
      return;
    }

    append({ value: "" });
  };

  const handleRemoveSelected = async () => {
    if (selectedIndexes.length === 0) return;

    if (selectedIndexes.length === fields.length) {
      //  Reset with a single empty row
      setValue("subTasks", [{ value: "" }]); // Replace all rows
      setDeletedSubtaskIds((prev) => [
        ...prev,
        ...fields
          .filter(field => field.SubtaskId)
          .map(field => field.SubtaskId)
      ]);
      setSelectedIndexes([]);
      await trigger("subTasks"); // Trigger validation
      return;
    }

    // Otherwise, remove selected rows only
    const updatedFields = fields.filter((_, index) => !selectedIndexes.includes(index));

    setValue("subTasks", updatedFields);
    setDeletedSubtaskIds((prev) => [
      ...prev,
      ...fields
        .filter((_, index) => selectedIndexes.includes(index))
        .filter(field => field.SubtaskId)
        .map(field => field.SubtaskId)
    ]);
    setSelectedIndexes([]);
    await trigger("subTasks");
  };

  const toggleSelect = (index) => {
    setSelectedIndexes((prev) =>
      prev.includes(index)
        ? prev.filter((i) => i !== index)
        : [...prev, index]
    );
  };

  const handleDiscard = () => {
    // Directly reset the form to default without validation
    reset(); // optional, in case you want to discard values
    navigate(`${paths.taskDetails.replace(":taskId", taskId.toLowerCase())}`);
  };

  const onSubmit = async (data) => {
    if (isSaving) return; // prevent double-click
    setIsSaving(true);

    try {
      const userId = localStorage.getItem("UserId") || "Unknown";

      // 1. Update the task
      const updatedTask = {
        taskName: data.task,
        subtaskNumber: data.subTasks.length,
        updatedBy: userId,
      };

      const updateTaskRes = await axios.patch(
        `${process.env.REACT_APP_API_URL}/taskRoute/updateTask/${taskId}`,
        updatedTask,{
          headers: {
            Authorization: process.env.REACT_APP_BasicAuthSecret
          }
        }
      );

      if (updateTaskRes.status === 200) {
        // 2. Delete removed subtasks
        for (const id of deletedSubtaskIds) {
          await axios.delete(
            `${process.env.REACT_APP_API_URL}/subtaskRoute/deletSubtaskById/${id}`,{
              headers: {
                Authorization: process.env.REACT_APP_BasicAuthSecret
              }
            }
          );
        }

        // 3. Separate subtasks
        const existingSubtasks = data.subTasks.filter(sub => sub.SubtaskId);

        const newSubtasks = data.subTasks
          .filter(sub => !sub.SubtaskId)
          .map(sub => ({
            SubtaskId: generateGUID(),
            TaskId: taskId,
            Subtask: sub.value,
            CreatedBy: userId,
          }));

        // 4. Update only changed existing subtasks
        for (const subtask of existingSubtasks) {
          if (subtask.value !== subtask.originalValue) {
            await axios.patch(
              `${process.env.REACT_APP_API_URL}/subtaskRoute/updateSubtask/${subtask.SubtaskId}`,
              {
                subtask: subtask.value,
                createdBy: userId,
              },
              {
                headers: {
                  Authorization: process.env.REACT_APP_BasicAuthSecret
                }
              }
            );
          }
        }

        // 5. Insert new subtasks in bulk
        if (newSubtasks.length > 0) {
          await axios.post(
            `${process.env.REACT_APP_API_URL}/subtaskRoute/subtasks`,
            { subtasks: newSubtasks },
            {
              headers: {
                Authorization: process.env.REACT_APP_BasicAuthSecret
              }
            }
          );
        }

        toast.success(t("taskAndSubtasksUpdatedSuccessMessage"));
        setDeletedSubtaskIds([]);
        navigate(`${paths.taskDetails.replace(":taskId", taskId.toLowerCase())}`);
      } else {
        toast.error(t("taskUpdateFailedMessage"));
      }
    } catch (error) {
      console.error("Update error:", error);
      toast.error(t("taskUpdateErrorMessage"));
    }

  };

  const loadingOrRedirect = handleLoadingAndAccess(
    isLoading,
    hasAccess,
  );

  if (loadingOrRedirect) {
    return loadingOrRedirect; // Render the loading spinner or handle navigation
  }

  return (
    <FormProvider {...methods}>
      <Row className="g-3 mb-3">
        <Col lg={12}>
          <Card className="mb-3 h-100">
            <Background image={corner4} className="bg-card" />
            <Card.Body className="position-relative pe-4 pb-2">
              <Row className="flex-between-center">
                <Col md>
                  <h5>{t("editTask")}</h5>

                  <Breadcrumb>
                    <Breadcrumb.Item
                      as={Link}
                      to="/task"
                      className="fw-semi-bold mb-1 fs-9 data-font-colour"
                      active
                    >
                      {t("task")}
                    </Breadcrumb.Item>
                    <Breadcrumb.Item
                      onClick={() =>
                        navigate(
                          `${paths.taskDetails.replace(
                            ":taskId",
                            taskData?.TaskId.toLowerCase() || taskId.toLowerCase() // fallback to taskId if TaskId is missing
                          )}`
                        )
                      }
                      className="fw-semi-bold mb-1 fs-9 cursor-pointer data-font-colour"
                      active
                    >
                      {taskData?.TaskName}
                    </Breadcrumb.Item>

                    <Breadcrumb.Item
                      className="fw-bold mb-1 fs-9 data-font-colour"
                      active
                    >
                      {t("edit")}
                    </Breadcrumb.Item>
                  </Breadcrumb>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {loading ? (
        <div className="text-center my-5">
          <Spinner animation="border" />
        </div>
      ) : (
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Card className="mb-3">
            <Card.Header
              as="h5"
              className="bg-body-tertiary data-font-colour"
            >
              {/* {t("taskDetails")} */}
              <Row className="align-items-center">
                <Col>
                  <h5 className="bg-body-tertiary data-font-colour">{t("taskDetails")}</h5>
                </Col>
              </Row>
            </Card.Header>
            <Card.Body>
              <Row>
                {/* Task Name */}
                <Col md="2" className="mb-2 data-font-size data-font-colour">
                  <Form.Label className="data-font-size">
                    {t("taskName")}
                    <span className="text-danger">*</span>
                  </Form.Label>
                </Col>
                <Col md="4" className="mb-2 data-font-size data-font-colour">
                  <Form.Group>
                    <Form.Control
                      type="text"
                      size="md"
                      {...register("task")}
                      isInvalid={!!errors.task}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.task?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={12}>
                  <Form.Group className="mt-2">
                    <Table hover bordered className="table-sm">
                      <thead>
                        <tr>
                          <th
                            style={{ width: "40px" }}
                            className="text-center"
                            onClick={() => {
                              if (selectedIndexes.length === fields.length) {
                                setSelectedIndexes([]);
                              } else {
                                setSelectedIndexes(fields.map((_, idx) => idx));
                              }
                            }}
                          >
                            <Form.Check
                              type="checkbox"
                              checked={
                                fields.length > 0 &&
                                selectedIndexes.length === fields.length
                              }
                              disabled={fields.length === 0}
                              className="checkbox-input"
                              onClick={(e) => {
                                e.stopPropagation(); // Prevent triggering <th> twice
                                if (selectedIndexes.length === fields.length) {
                                  setSelectedIndexes([]);
                                } else {
                                  setSelectedIndexes(fields.map((_, idx) => idx));
                                }
                              }}
                            />
                          </th>

                          <th className="text-center" style={{ position: "relative" }}>
                            {t("subtask")}
                            <div className="float-end">
                              <Button
                                variant="outline-danger"
                                size="sm"
                                disabled={selectedIndexes.length === 0}
                                onClick={handleRemoveSelected}
                                title={t("deleteSelected")}
                              >
                                <i className="fa fa-trash" />
                              </Button>
                            </div>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {fields.map((field, index) => (
                          <tr key={field.id}>
                            <td
                              className="text-center align-middle"
                              onClick={() => toggleSelect(index)}
                            >
                              <Form.Check
                                type="checkbox"
                                checked={selectedIndexes.includes(index)}
                                readOnly
                                className="checkbox-input"
                                onClick={(e) => {
                                  e.stopPropagation(); // Prevent <td> onClick from firing twice
                                  toggleSelect(index);
                                }}
                              />
                            </td>

                            <td>
                              <Form.Control
                                type="text"
                                {...register(`subTasks.${index}.value`)}
                                isInvalid={!!errors.subTasks?.[index]?.value}
                                // onBlur={() => {
                                //   // Ensure validation triggers after blur (especially for empty input)
                                //   trigger(`subTasks.${index}.value`);
                                // }}
                                onKeyDown={async (e) => {
                                  if (e.key === "Enter") {
                                    e.preventDefault();
                                    const isValid = await trigger(`subTasks.${index}.value`);
                                    const currentValue = watch(`subTasks.${index}.value`);

                                    if (isValid && currentValue.trim()) {
                                      append({ value: "" }, { shouldFocus: false });
                                    }
                                  }
                                }}
                              />
                              <Form.Control.Feedback type="invalid">
                                {errors.subTasks?.[index]?.value?.message}
                              </Form.Control.Feedback>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                    <div className="d-flex  mb-3">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="me-2"
                        onClick={handleAddSubtask}
                        title={t("addSubtask")}
                      >
                        <i className="fa fa-plus" /> {t("addSubtask")}
                      </Button>
                    </div>
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>

            <Divider />
            <Card.Body className="pt-0">
              <Row className="justify-content-end">
                <Col xs="auto">
                  <Button
                    variant="falcon-default"
                    size="sm"
                    className="me-3"
                    type="button" //  NOT "submit"
                    onClick={handleDiscard}
                  >
                    {t("cancel")}
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    type="submit"
                    disabled={isSaving}
                  >
                    {isSaving ? t("saving") : t("save")}
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Form>
      )}

    </FormProvider>

  );
};

export default EditTask;
